/** Host roles for the hosted SaaS SPA (not Railway OSS). */

export type HostMode = "marketing" | "command" | "docs" | "local";

export const COMMAND_ORIGIN =
  (import.meta.env.VITE_COMMAND_ORIGIN as string | undefined)?.replace(
    /\/$/,
    ""
  ) || "https://command.opengateways.xyz";

export const MARKETING_ORIGIN =
  (import.meta.env.VITE_MARKETING_ORIGIN as string | undefined)?.replace(
    /\/$/,
    ""
  ) || "https://opengateways.xyz";

export const DOCS_ORIGIN =
  (import.meta.env.VITE_DOCS_ORIGIN as string | undefined)?.replace(
    /\/$/,
    ""
  ) || "https://docs.opengateways.xyz";

/** Prefer custom API host; override with VITE_EDGE_API_URL at build. */
export const EDGE_API =
  (import.meta.env.VITE_EDGE_API_URL as string | undefined)?.replace(
    /\/$/,
    ""
  ) || "https://api.opengateways.xyz";

/** Stable workers.dev fallback if custom domain is still propagating */
export const EDGE_API_FALLBACK =
  "https://opengateways-edge-api.matt-ed6.workers.dev";

/** Apex hub (shared fallback). Branded orgs use {slug}.hub.opengateways.xyz */
export const HUB_APEX =
  (import.meta.env.VITE_OPENGATEWAY_HUB_URL as string | undefined)
    ?.replace(/\/$/, "")
    .replace(/^https?:\/\//, "") || "hub.opengateways.xyz";

/**
 * Normalize a display name or slug into a DNS-safe org slug.
 * "MR Dula Solutions" → "mrdulasolutions"
 */
export function slugifyOrgName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-")
    .slice(0, 48)
    .replace(/-$/, "") || "org";
}

/** Host only: mrdulasolutions.hub.opengateways.xyz */
export function brandedHubHost(orgSlug: string): string {
  const slug = slugifyOrgName(orgSlug);
  // hub apex is "hub.opengateways.xyz" → "{slug}.hub.opengateways.xyz"
  if (HUB_APEX.startsWith("hub.")) {
    return `${slug}.${HUB_APEX}`;
  }
  return `${slug}.hub.opengateways.xyz`;
}

/** Full https URL for an org's branded gateway */
export function brandedHubUrl(orgSlug: string): string {
  return `https://${brandedHubHost(orgSlug)}`;
}

/**
 * Resolve product surface from hostname.
 * Local override: VITE_FORCE_HOST_MODE=docs|command|marketing
 * or ?host=docs on localhost for docs UI without /etc/hosts.
 */
export function hostMode(hostname = window.location.hostname): HostMode {
  const forced = (
    import.meta.env.VITE_FORCE_HOST_MODE as string | undefined
  )
    ?.trim()
    .toLowerCase();
  if (
    forced === "docs" ||
    forced === "command" ||
    forced === "marketing" ||
    forced === "local"
  ) {
    return forced;
  }
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("host");
    if (q === "docs" || q === "command" || q === "marketing") {
      return q;
    }
  }
  if (
    hostname === "docs.opengateways.xyz" ||
    hostname.startsWith("docs.")
  ) {
    return "docs";
  }
  if (
    hostname === "command.opengateways.xyz" ||
    hostname.startsWith("command.")
  ) {
    return "command";
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "local";
  }
  return "marketing";
}

export function docsUrl(path = "/"): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${DOCS_ORIGIN}${p}`;
}

export function commandAppUrl(path = "/"): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${COMMAND_ORIGIN}${p}`;
}

/** Public OSS monorepo (not this private SaaS repo). */
export const GITHUB_OSS =
  (import.meta.env.VITE_GITHUB_OSS as string | undefined)?.replace(/\/$/, "") ||
  "https://github.com/mrdulasolutions/open-gateway";

/** Official Railway one-click template — self-host only, not SaaS. */
export const RAILWAY_DEPLOY =
  (import.meta.env.VITE_RAILWAY_DEPLOY as string | undefined)?.replace(
    /\/$/,
    ""
  ) ||
  "https://railway.com/deploy/open-gateway?utm_medium=integration&utm_source=docs&utm_campaign=opengateway";

export const RAILWAY_TEMPLATE_URL =
  "https://railway.com/deploy/open-gateway";
